import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, SearchIcon, CheckCircleIcon, PackageIcon, ScanLineIcon, PlayIcon, PauseIcon, XCircleIcon, ClockIcon, Leaf, DropletIcon, FlaskConicalIcon } from 'lucide-react';
interface MixerModuleProps {
  onBackToOverview?: () => void;
}
interface Material {
  id: string;
  name: string;
  type: 'spice' | 'oil' | 'flour' | 'other';
  quantity: number;
  unit: string;
  location: string;
  scanned: boolean;
  ready: boolean; // New property to track if material is ready from pre-process
}
interface MixerOrder {
  id: string;
  name: string;
  status: 'pending' | 'in-progress' | 'completed' | 'paused';
  deadline: Date;
  startTime?: Date;
  duration?: number; // in seconds
  materials: Material[];
}
export function MixerModule({
  onBackToOverview
}: MixerModuleProps) {
  const [view, setView] = useState<'list' | 'detail' | 'scan' | 'production'>('list');
  const [selectedOrder, setSelectedOrder] = useState<MixerOrder | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [scannedCode, setScannedCode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanSuccess, setScanSuccess] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);
  const [materialToScan, setMaterialToScan] = useState<Material | null>(null);
  const [showScanPopup, setShowScanPopup] = useState(false);
  const [scanningInProgress, setScanningInProgress] = useState(false);
  const [scanPopupSuccess, setScanPopupSuccess] = useState<boolean | null>(null);
  const [scanPopupError, setScanPopupError] = useState('');
  const [scanPopupCode, setScanPopupCode] = useState('');
  // Sample orders with some materials marked as ready
  const [mixerOrders, setMixerOrders] = useState<MixerOrder[]>([{
    id: 'M1001',
    name: 'Gustoria Paella',
    status: 'pending',
    deadline: new Date(new Date().getTime() + 60 * 60000),
    materials: [{
      id: 'S001',
      name: 'Safran',
      type: 'spice',
      quantity: 0.05,
      unit: 'kg',
      location: 'Gewürzlager A3',
      scanned: false,
      ready: true // Ready from spice kitchen
    }, {
      id: 'R151501',
      name: 'Rapsöl raffiniert',
      type: 'oil',
      quantity: 1.5,
      unit: 'l',
      location: 'Öllager E1',
      scanned: false,
      ready: false
    }, {
      id: 'R161215',
      name: 'Langkornreis',
      type: 'flour',
      quantity: 5,
      unit: 'kg',
      location: 'Trockenlager C3',
      scanned: false,
      ready: true // Ready from pre-process
    }]
  }, {
    id: 'M1002',
    name: 'Gustoria Risotto',
    status: 'in-progress',
    deadline: new Date(new Date().getTime() + 30 * 60000),
    startTime: new Date(new Date().getTime() - 15 * 60000),
    duration: 15 * 60,
    materials: [{
      id: 'S004',
      name: 'Basilikum getrocknet',
      type: 'spice',
      quantity: 0.15,
      unit: 'kg',
      location: 'Gewürzlager A2',
      scanned: true,
      ready: true
    }, {
      id: 'R151501',
      name: 'Rapsöl raffiniert',
      type: 'oil',
      quantity: 1.2,
      unit: 'l',
      location: 'Öllager E1',
      scanned: true,
      ready: true
    }, {
      id: 'R161215',
      name: 'Langkornreis',
      type: 'flour',
      quantity: 4.5,
      unit: 'kg',
      location: 'Trockenlager C3',
      scanned: true,
      ready: true
    }]
  }, {
    id: 'M1003',
    name: 'Gustoria Bolognese',
    status: 'completed',
    deadline: new Date(new Date().getTime() - 30 * 60000),
    startTime: new Date(new Date().getTime() - 90 * 60000),
    duration: 60 * 60,
    materials: [{
      id: 'S005',
      name: 'Oregano',
      type: 'spice',
      quantity: 0.1,
      unit: 'kg',
      location: 'Gewürzlager A1',
      scanned: true,
      ready: true
    }, {
      id: 'R151501',
      name: 'Rapsöl raffiniert',
      type: 'oil',
      quantity: 2,
      unit: 'l',
      location: 'Öllager E1',
      scanned: true,
      ready: true
    }, {
      id: 'R161220',
      name: 'Hartweizengriess',
      type: 'flour',
      quantity: 6,
      unit: 'kg',
      location: 'Trockenlager C2',
      scanned: true,
      ready: true
    }]
  }]);
  // Filter orders based on search term
  const filteredOrders = mixerOrders.filter(order => order.name.toLowerCase().includes(searchTerm.toLowerCase()) || order.id.includes(searchTerm));
  // Handle order selection
  const handleOrderSelect = (order: MixerOrder) => {
    setSelectedOrder(order);
    setView('detail');
  };
  // Handle back to list
  const handleBackToList = () => {
    setView('list');
    setSelectedOrder(null);
    stopTimer();
  };
  // Handle back to detail
  const handleBackToDetail = () => {
    setView('detail');
  };
  // Handle scan mode
  const handleScanMode = () => {
    setView('scan');
    setScannedCode('');
    setScanSuccess(null);
    setErrorMessage('');
  };
  // Handle scan
  const handleScan = () => {
    if (!scannedCode || !selectedOrder) {
      setErrorMessage('Bitte geben Sie einen Barcode ein oder scannen Sie ihn');
      setScanSuccess(false);
      return;
    }
    setIsScanning(true);
    // Simulate scanning process
    setTimeout(() => {
      // Check if the scanned code matches any material
      const materialIndex = selectedOrder.materials.findIndex(material => scannedCode.includes(material.id) || scannedCode.toLowerCase().includes(material.name.toLowerCase()));
      if (materialIndex !== -1 && !selectedOrder.materials[materialIndex].scanned) {
        // Update the scanned status
        const updatedOrder = {
          ...selectedOrder
        };
        updatedOrder.materials[materialIndex].scanned = true;
        setSelectedOrder(updatedOrder);
        // Update in the orders list
        const updatedOrders = mixerOrders.map(order => order.id === updatedOrder.id ? updatedOrder : order);
        setMixerOrders(updatedOrders);
        setScanSuccess(true);
        setErrorMessage('');
      } else if (materialIndex !== -1 && selectedOrder.materials[materialIndex].scanned) {
        setScanSuccess(false);
        setErrorMessage('Dieses Material wurde bereits gescannt!');
      } else {
        setScanSuccess(false);
        setErrorMessage('Der gescannte Code gehört zu keinem benötigten Material.');
      }
      setScannedCode('');
      setIsScanning(false);
    }, 1000);
  };
  // Check if all materials are scanned
  const allMaterialsScanned = selectedOrder ? selectedOrder.materials.every(material => material.scanned) : false;
  // Start production
  const handleStartProduction = () => {
    if (!selectedOrder) return;
    const updatedOrder = {
      ...selectedOrder
    };
    updatedOrder.status = 'in-progress';
    updatedOrder.startTime = new Date();
    updatedOrder.duration = 0;
    setSelectedOrder(updatedOrder);
    // Update in the orders list
    const updatedOrders = mixerOrders.map(order => order.id === updatedOrder.id ? updatedOrder : order);
    setMixerOrders(updatedOrders);
    startTimer();
    setView('list');
  };
  // Pause production
  const handlePauseProduction = () => {
    if (!selectedOrder) return;
    const updatedOrder = {
      ...selectedOrder
    };
    if (updatedOrder.status === 'paused') {
      updatedOrder.status = 'in-progress';
      startTimer();
    } else {
      updatedOrder.status = 'paused';
      stopTimer();
    }
    setSelectedOrder(updatedOrder);
    // Update in the orders list
    const updatedOrders = mixerOrders.map(order => order.id === updatedOrder.id ? updatedOrder : order);
    setMixerOrders(updatedOrders);
  };
  // Complete production
  const handleCompleteProduction = () => {
    if (!selectedOrder) return;
    const updatedOrder = {
      ...selectedOrder
    };
    updatedOrder.status = 'completed';
    setSelectedOrder(updatedOrder);
    // Update in the orders list
    const updatedOrders = mixerOrders.map(order => order.id === updatedOrder.id ? updatedOrder : order);
    setMixerOrders(updatedOrders);
    stopTimer();
    setView('list');
  };
  // Timer functions
  const startTimer = () => {
    if (timerInterval) clearInterval(timerInterval);
    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    setTimerInterval(interval);
  };
  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
  };
  // Update timer when viewing an in-progress order
  useEffect(() => {
    if (selectedOrder && selectedOrder.status === 'in-progress') {
      if (selectedOrder.duration !== undefined) {
        setElapsedTime(selectedOrder.duration);
      }
      startTimer();
    } else {
      stopTimer();
    }
    return () => {
      stopTimer();
    };
  }, [selectedOrder]);
  // Update order duration
  useEffect(() => {
    if (selectedOrder && (selectedOrder.status === 'in-progress' || selectedOrder.status === 'paused')) {
      const updatedOrder = {
        ...selectedOrder,
        duration: elapsedTime
      };
      // Update in the orders list without triggering re-renders
      setMixerOrders(prev => prev.map(order => order.id === updatedOrder.id ? updatedOrder : order));
    }
  }, [elapsedTime]);
  // Format time for display
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor(seconds % 3600 / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  // Material type icons
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'spice':
        return <Leaf size={16} className="text-green-600" data-id="element-679" />;
      case 'oil':
        return <DropletIcon size={16} className="text-amber-600" data-id="element-680" />;
      case 'flour':
        return <PackageIcon size={16} className="text-blue-600" data-id="element-681" />;
      default:
        return <FlaskConicalIcon size={16} className="text-purple-600" data-id="element-682" />;
    }
  };
  // Handle opening the scan popup for a specific material
  const handleOpenScanPopup = (material: Material) => {
    setMaterialToScan(material);
    setShowScanPopup(true);
    setScanPopupCode('');
    setScanPopupSuccess(null);
    setScanPopupError('');
  };
  // Handle closing the scan popup
  const handleCloseScanPopup = () => {
    setShowScanPopup(false);
    setMaterialToScan(null);
    setScanPopupSuccess(null);
  };
  // Handle scanning in the popup
  const handleScanInPopup = () => {
    if (!scanPopupCode || !materialToScan || !selectedOrder) {
      setScanPopupError('Bitte geben Sie einen Barcode ein oder scannen Sie ihn');
      setScanPopupSuccess(false);
      return;
    }
    setScanningInProgress(true);
    // Simulate scanning process
    setTimeout(() => {
      // Check if the scanned code matches the material
      if (scanPopupCode.includes(materialToScan.id) || scanPopupCode.toLowerCase().includes(materialToScan.name.toLowerCase())) {
        // Update the scanned status
        const updatedOrder = {
          ...selectedOrder
        };
        const materialIndex = updatedOrder.materials.findIndex(m => m.id === materialToScan.id);
        if (materialIndex !== -1) {
          updatedOrder.materials[materialIndex].scanned = true;
          setSelectedOrder(updatedOrder);
          // Update in the orders list
          const updatedOrders = mixerOrders.map(order => order.id === updatedOrder.id ? updatedOrder : order);
          setMixerOrders(updatedOrders);
          setScanPopupSuccess(true);
          setScanPopupError('');
          // Close popup after success
          setTimeout(() => {
            handleCloseScanPopup();
          }, 1500);
        }
      } else {
        setScanPopupSuccess(false);
        setScanPopupError('Der gescannte Code passt nicht zu diesem Material.');
      }
      setScanningInProgress(false);
    }, 1000);
  };
  return <div className="container mx-auto" data-id="element-683">
      {/* Header */}
      <div className="mb-8" data-id="element-684">
        <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-685">Mischer</h2>
        <div className="flex items-center justify-between" data-id="element-686">
          <p className="text-gray-600" data-id="element-687">
            Steuerung und Überwachung der Mischprozesse
          </p>
          {onBackToOverview}
        </div>
      </div>

      {/* Order List View */}
      {view === 'list' && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-688">
          <div className="mb-6" data-id="element-689">
            <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-690">
              Heutige Mischaufträge
            </h3>
            <p className="text-gray-600" data-id="element-691">
              Wählen Sie einen Auftrag aus, um mit dem Mischprozess zu beginnen
            </p>
          </div>

          {/* Search bar */}
          <div className="relative mb-6" data-id="element-692">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-693">
              <SearchIcon size={18} className="text-gray-400" data-id="element-694" />
            </div>
            <input type="text" className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md" placeholder="Nach Auftrag suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-695" />
          </div>

          {/* Orders list */}
          <div className="space-y-4" data-id="element-696">
            {filteredOrders.length > 0 ? filteredOrders.map(order => <div key={order.id} className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer 
                    ${order.status === 'completed' ? 'bg-green-50 border-green-200' : order.status === 'in-progress' ? 'bg-blue-50 border-blue-200' : order.status === 'paused' ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`} onClick={() => handleOrderSelect(order)} data-id="element-697">
                  <div className="flex justify-between items-start" data-id="element-698">
                    <div data-id="element-699">
                      <h4 className="font-medium text-gray-900" data-id="element-700">
                        {order.name}
                      </h4>
                      <p className="text-sm text-gray-500" data-id="element-701">
                        Auftrags-Nr: #{order.id}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-702">
                        Bereitstellung bis:{' '}
                        {order.deadline.toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}{' '}
                        Uhr
                      </p>
                      {(order.status === 'in-progress' || order.status === 'paused') && order.duration !== undefined && <p className="text-sm text-blue-600 flex items-center mt-1" data-id="element-703">
                            <ClockIcon size={14} className="mr-1" data-id="element-704" />
                            Laufzeit: {formatTime(order.duration)}
                          </p>}
                    </div>
                    <div className="flex items-center" data-id="element-705">
                      {order.status === 'completed' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-706">
                          <CheckCircleIcon size={12} className="mr-1" data-id="element-707" />
                          Abgeschlossen
                        </span> : order.status === 'in-progress' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-708">
                          <PlayIcon size={12} className="mr-1" data-id="element-709" />
                          In Bearbeitung
                        </span> : order.status === 'paused' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-id="element-710">
                          <PauseIcon size={12} className="mr-1" data-id="element-711" />
                          Pausiert
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-712">
                          <PackageIcon size={12} className="mr-1" data-id="element-713" />
                          Ausstehend
                        </span>}
                    </div>
                  </div>

                  {/* Materials progress */}
                  <div className="mt-3" data-id="element-714">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-715">
                      Materialien:{' '}
                      {order.materials.filter(m => m.scanned).length} von{' '}
                      {order.materials.length} bereitgestellt
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-716">
                      <div className="bg-[#8DC63F] h-2.5 rounded-full" style={{
                width: `${order.materials.filter(m => m.scanned).length / order.materials.length * 100}%`
              }} data-id="element-717"></div>
                    </div>
                  </div>
                </div>) : <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-200" data-id="element-718">
                <PackageIcon size={40} className="mx-auto text-gray-400 mb-3" data-id="element-719" />
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-720">
                  Keine Mischaufträge
                </h3>
                <p className="text-gray-500" data-id="element-721">
                  Für heute sind keine Mischaufträge geplant.
                </p>
              </div>}
          </div>
        </div>}

      {/* Order Detail View */}
      {view === 'detail' && selectedOrder && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-722">
          <button onClick={handleBackToList} className="flex items-center gap-2 mb-6 text-gray-600 hover:text-gray-800" data-id="element-723">
            <ArrowLeftIcon size={16} data-id="element-724" />
            <span data-id="element-725">Zurück zur Übersicht</span>
          </button>

          <div className="mb-6" data-id="element-726">
            <div className="flex justify-between items-start" data-id="element-727">
              <div data-id="element-728">
                <h3 className="text-xl font-bold text-gray-800 mb-1" data-id="element-729">
                  {selectedOrder.name}
                </h3>
                <p className="text-gray-600" data-id="element-730">
                  Auftrags-Nr: #{selectedOrder.id} | Bereitstellung bis:{' '}
                  {selectedOrder.deadline.toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit'
              })}{' '}
                  Uhr
                </p>
                {(selectedOrder.status === 'in-progress' || selectedOrder.status === 'paused') && selectedOrder.duration !== undefined && <p className="text-sm text-blue-600 flex items-center mt-1" data-id="element-731">
                      <ClockIcon size={14} className="mr-1" data-id="element-732" />
                      Laufzeit: {formatTime(selectedOrder.duration)}
                    </p>}
              </div>
              <div data-id="element-733">
                {selectedOrder.status === 'completed' ? <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-734">
                    <CheckCircleIcon size={16} className="mr-1" data-id="element-735" />
                    Abgeschlossen
                  </span> : selectedOrder.status === 'in-progress' ? <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-736">
                    <PlayIcon size={16} className="mr-1" data-id="element-737" />
                    In Bearbeitung
                  </span> : selectedOrder.status === 'paused' ? <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800" data-id="element-738">
                    <PauseIcon size={16} className="mr-1" data-id="element-739" />
                    Pausiert
                  </span> : <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800" data-id="element-740">
                    <PackageIcon size={16} className="mr-1" data-id="element-741" />
                    Ausstehend
                  </span>}
              </div>
            </div>
          </div>

          {/* Materials list */}
          <div className="mb-6" data-id="element-742">
            <h4 className="text-lg font-semibold text-gray-800 mb-3" data-id="element-743">
              Benötigte Materialien
            </h4>
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-4" data-id="element-744">
              <div className="text-sm text-gray-600 mb-2" data-id="element-745">
                Fortschritt:{' '}
                {selectedOrder.materials.filter(m => m.scanned).length} von{' '}
                {selectedOrder.materials.length} Materialien bereitgestellt
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4" data-id="element-746">
                <div className="bg-[#8DC63F] h-2.5 rounded-full" style={{
              width: `${selectedOrder.materials.filter(m => m.scanned).length / selectedOrder.materials.length * 100}%`
            }} data-id="element-747"></div>
              </div>
              <div className="space-y-3" data-id="element-748">
                {selectedOrder.materials.map(material => <div key={material.id} className={`p-4 rounded-lg border ${material.scanned ? 'bg-green-50 border-green-200' : material.ready ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`} data-id="element-749">
                    <div className="flex justify-between" data-id="element-750">
                      <div data-id="element-751">
                        <div className="flex items-center" data-id="element-752">
                          {getTypeIcon(material.type)}
                          <h5 className="font-medium text-gray-900 ml-2" data-id="element-753">
                            {material.name}
                          </h5>
                          {material.ready && !material.scanned && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full" data-id="element-754">
                              Bereit
                            </span>}
                        </div>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-755">
                          {material.quantity} {material.unit} • Artikel-Nr:{' '}
                          {material.id}
                        </p>
                      </div>
                      <div className="flex flex-col items-end" data-id="element-756">
                        {material.scanned ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-757">
                            <CheckCircleIcon size={12} className="mr-1" data-id="element-758" />
                            Hinzugefügt
                          </span> : material.ready ? <button onClick={() => handleOpenScanPopup(material)} className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-md font-medium" data-id="element-759">
                            Rohstoff einscannen
                          </button> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-760">
                            <PackageIcon size={12} className="mr-1" data-id="element-761" />
                            Ausstehend
                          </span>}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3 justify-center" data-id="element-762">
            {selectedOrder.status === 'pending' && <button onClick={handleStartProduction} disabled={!selectedOrder.materials.some(m => m.scanned) || allMaterialsScanned} className={`bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${!selectedOrder.materials.some(m => m.scanned) ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-763">
                <PlayIcon size={18} data-id="element-764" />
                <span data-id="element-765">Produktion starten</span>
              </button>}
            {selectedOrder.status === 'pending' && allMaterialsScanned && <button onClick={handleStartProduction} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2" data-id="element-766">
                <PlayIcon size={18} data-id="element-767" />
                <span data-id="element-768">Produktion starten</span>
              </button>}
            {selectedOrder.status === 'in-progress' && <>
                <button onClick={handlePauseProduction} className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2" data-id="element-769">
                  <PauseIcon size={18} data-id="element-770" />
                  <span data-id="element-771">Produktion pausieren</span>
                </button>
                <button onClick={handleCompleteProduction} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2" data-id="element-772">
                  <CheckCircleIcon size={18} data-id="element-773" />
                  <span data-id="element-774">Produktion abschließen</span>
                </button>
              </>}
            {selectedOrder.status === 'paused' && <>
                <button onClick={handlePauseProduction} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2" data-id="element-775">
                  <PlayIcon size={18} data-id="element-776" />
                  <span data-id="element-777">Produktion fortsetzen</span>
                </button>
                <button onClick={handleCompleteProduction} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2" data-id="element-778">
                  <CheckCircleIcon size={18} data-id="element-779" />
                  <span data-id="element-780">Produktion abschließen</span>
                </button>
              </>}
            {selectedOrder.status === 'completed' && <div className="text-center w-full" data-id="element-781">
                <p className="text-green-600 font-medium" data-id="element-782">
                  Dieser Auftrag wurde erfolgreich abgeschlossen.
                </p>
              </div>}
          </div>
        </div>}

      {/* Scan Materials View */}
      {view === 'scan' && selectedOrder && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-783">
          <button onClick={handleBackToDetail} className="flex items-center gap-2 mb-6 text-gray-600 hover:text-gray-800" data-id="element-784">
            <ArrowLeftIcon size={16} data-id="element-785" />
            <span data-id="element-786">Zurück zum Auftrag</span>
          </button>

          <div className="mb-6" data-id="element-787">
            <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-788">
              Materialien scannen für {selectedOrder.name}
            </h3>
            <p className="text-gray-600" data-id="element-789">
              Bitte scannen Sie die Materialien, die Sie zur Mischanlage
              gebracht haben.
            </p>
          </div>

          {/* Scan area */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6" data-id="element-790">
            <h4 className="font-medium text-gray-800 mb-4 flex items-center" data-id="element-791">
              <ScanLineIcon size={20} className="mr-2" data-id="element-792" />
              Materialien scannen
            </h4>
            <div className="flex flex-col md:flex-row gap-3 mb-4" data-id="element-793">
              <div className="relative flex-grow" data-id="element-794">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-795">
                  <ScanLineIcon size={18} className="text-gray-400" data-id="element-796" />
                </div>
                <input type="text" className={`pl-10 pr-4 py-2 w-full border ${scanSuccess === true ? 'border-green-500 bg-green-50' : scanSuccess === false ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-md`} placeholder="Barcode scannen..." value={scannedCode} onChange={e => setScannedCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleScan()} data-id="element-797" />
              </div>
              <button onClick={handleScan} disabled={isScanning} className={`py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium ${isScanning ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-798">
                {isScanning ? 'Scanne...' : 'Scannen'}
              </button>
            </div>
            {scanSuccess === true && <div className="mb-4 flex items-center text-green-600 text-sm" data-id="element-799">
                <CheckCircleIcon size={16} className="mr-1" data-id="element-800" />
                <span data-id="element-801">Material erfolgreich gescannt!</span>
              </div>}
            {scanSuccess === false && <div className="mb-4 flex items-center text-red-600 text-sm" data-id="element-802">
                <XCircleIcon size={16} className="mr-1" data-id="element-803" />
                <span data-id="element-804">{errorMessage}</span>
              </div>}

            {/* Materials checklist */}
            <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-805">
              <h5 className="font-medium text-gray-800 mb-3" data-id="element-806">
                Material-Checkliste:
              </h5>
              <div className="space-y-2" data-id="element-807">
                {selectedOrder.materials.map(material => <div key={material.id} className={`flex items-center p-3 rounded-lg border ${material.scanned ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`} data-id="element-808">
                    <div className={`w-5 h-5 rounded-full mr-3 flex items-center justify-center ${material.scanned ? 'bg-green-500 text-white' : 'border border-gray-400'}`} data-id="element-809">
                      {material.scanned && <CheckCircleIcon size={14} data-id="element-810" />}
                    </div>
                    <div className="flex-1" data-id="element-811">
                      <div className="flex items-center" data-id="element-812">
                        {getTypeIcon(material.type)}
                        <div className="font-medium ml-2" data-id="element-813">{material.name}</div>
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-814">
                        {material.quantity} {material.unit} • Artikel-Nr:{' '}
                        {material.id}
                      </div>
                    </div>
                    <div data-id="element-815">
                      {material.scanned ? <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full" data-id="element-816">
                          Bereitgestellt
                        </span> : <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full" data-id="element-817">
                          Ausstehend
                        </span>}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-center" data-id="element-818">
            {allMaterialsScanned ? <button onClick={handleStartProduction} className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2" data-id="element-819">
                <PlayIcon size={18} data-id="element-820" />
                <span data-id="element-821">Produktion starten</span>
              </button> : <button onClick={handleBackToDetail} className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium" data-id="element-822">
                Zurück zum Auftrag
              </button>}
          </div>
        </div>}

      {/* Scan Material Popup */}
      {showScanPopup && materialToScan && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-823">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" data-id="element-824">
            <div className="flex justify-between items-center mb-4" data-id="element-825">
              <h3 className="text-lg font-bold text-gray-800" data-id="element-826">
                Material einscannen
              </h3>
              <button onClick={handleCloseScanPopup} className="text-gray-500 hover:text-gray-700" data-id="element-827">
                <XCircleIcon size={20} data-id="element-828" />
              </button>
            </div>
            <div className="mb-4" data-id="element-829">
              <div className="flex items-center mb-2" data-id="element-830">
                {getTypeIcon(materialToScan.type)}
                <p className="font-medium text-gray-900 ml-2" data-id="element-831">
                  {materialToScan.name}
                </p>
              </div>
              <p className="text-sm text-gray-600" data-id="element-832">
                Bitte scannen Sie den Barcode für {materialToScan.name}{' '}
                (Artikel-Nr: {materialToScan.id})
              </p>
            </div>
            <div className="mb-4" data-id="element-833">
              <div className="flex gap-2" data-id="element-834">
                <input type="text" className={`flex-1 px-3 py-2 border rounded-md ${scanPopupSuccess === true ? 'border-green-500 bg-green-50' : scanPopupSuccess === false ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} placeholder="Barcode scannen..." value={scanPopupCode} onChange={e => setScanPopupCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleScanInPopup()} data-id="element-835" />
                <button onClick={handleScanInPopup} disabled={scanningInProgress} className={`px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md ${scanningInProgress ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-836">
                  {scanningInProgress ? 'Scanne...' : 'Scannen'}
                </button>
              </div>
              {scanPopupSuccess === true && <div className="mt-2 text-green-600 flex items-center" data-id="element-837">
                  <CheckCircleIcon size={16} className="mr-1" data-id="element-838" />
                  <span data-id="element-839">Material erfolgreich gescannt!</span>
                </div>}
              {scanPopupSuccess === false && <div className="mt-2 text-red-600 flex items-center" data-id="element-840">
                  <XCircleIcon size={16} className="mr-1" data-id="element-841" />
                  <span data-id="element-842">{scanPopupError}</span>
                </div>}
            </div>
          </div>
        </div>}
    </div>;
}